// Chip editor for item_firearm_attributes.features (P1-18 Phase 2 + 3).
//
// Interaction:
//   - Each existing feature renders as a chip with an inline ✕ handle. Tap
//     ✕ to remove — removal is confirmed-by-undo (optimistic + no modal),
//     mirroring iOS Mail swipe-to-delete.
//   - A trailing "+" chip opens an inline input. Type a feature, press
//     Enter to add. Backspace on an empty input closes the input.
//   - Suggestions dropdown while adding (P1-18 Phase 3): the user's recent-
//     use entries (last ~20, localStorage) sit on top, then the catalog-
//     wide most-used feature strings pulled from the server via a 150ms-
//     debounced GET /catalog/features/suggest. Merged + ranked by the
//     shared mergeFeatureSuggestions() in @betterpos/domain.
//
// Validation runs on every keystroke — the add button is disabled until
// the trimmed input is 1..30 chars and the array is at or below 8.
//
// On save (either on-add or on-remove), the whole array is PATCHed —
// cheaper than a per-chip API and keeps optimistic rollback simple.

import {
  FEATURE_MAX_LEN,
  FEATURES_MAX_COUNT,
  mergeFeatureSuggestions,
  type SuggestionItem,
  validateFeatures,
} from "@betterpos/domain";
import { useEffect, useRef, useState } from "react";
import { readSessionToken } from "../../data/audit.ts";
import {
  type EditResult,
  type FeatureSuggestion,
  type FirearmFeaturesUpdate,
  fetchFeatureSuggestions,
  updateFirearmFeatures,
} from "../../data/catalog.ts";

const RECENT_KEY = "betterpos.catalog.recentFeatures";
const RECENT_MAX = 20;
const RECENT_CAP = 4;
const TOTAL_CAP = 8;
const AUTOCOMPLETE_DEBOUNCE_MS = 150;

type Props = {
  itemId: string;
  // The server's current version of item_firearm_attributes. The parent
  // bumps this on every successful save, which bubbles back here as a
  // prop change — our `version` state syncs via useEffect.
  expectedVersion: number;
  features: string[];
  onSaved: (next: FirearmFeaturesUpdate) => void;
};

export function FeaturesEditor({ itemId, expectedVersion, features, onSaved }: Props) {
  const [adding, setAdding] = useState(false);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recent, setRecent] = useState<string[]>(() => readRecent());
  const [catalog, setCatalog] = useState<FeatureSuggestion[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (adding) inputRef.current?.focus();
  }, [adding]);

  // Debounced catalog-wide autocomplete lookup. Only runs while the input
  // is open; cancels on unmount or when input changes before the timer
  // fires. Errors are swallowed — the dropdown falls back to recents-only.
  useEffect(() => {
    if (!adding) return;
    const token = readSessionToken();
    if (!token) return;
    const query = input.trim();
    const handle = window.setTimeout(async () => {
      const res = await fetchFeatureSuggestions(token, query, TOTAL_CAP + features.length);
      if (res.ok) setCatalog(res.suggestions);
    }, AUTOCOMPLETE_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [adding, input, features.length]);

  const atCap = features.length >= FEATURES_MAX_COUNT;
  const trimmed = input.trim();
  const dup = features.some((f) => f.toLowerCase() === trimmed.toLowerCase());
  const canAdd =
    trimmed.length >= 1 && trimmed.length <= FEATURE_MAX_LEN && !dup && !atCap && !saving;

  const suggestions: SuggestionItem[] = mergeFeatureSuggestions({
    recent,
    catalog,
    currentFeatures: features,
    query: input,
    recentCap: RECENT_CAP,
    totalCap: TOTAL_CAP,
  });

  const commit = async (next: string[]) => {
    const token = readSessionToken();
    if (!token) {
      setError("Not signed in.");
      return;
    }
    // Validate client-side so we never ship an obviously bad request.
    const v = validateFeatures(next);
    if (!v.ok) {
      setError("That value isn't valid.");
      return;
    }
    setSaving(true);
    setError(null);
    const res: EditResult<FirearmFeaturesUpdate> = await updateFirearmFeatures(token, itemId, {
      expectedVersion,
      features: v.value,
    });
    setSaving(false);
    if (!res.ok) {
      setError(messageForError(res));
      return;
    }
    onSaved(res.value);
  };

  const onAdd = async () => {
    if (!canAdd) return;
    const next = [...features, trimmed];
    setInput("");
    setAdding(false);
    pushRecent(trimmed, setRecent);
    await commit(next);
  };

  const onRemove = async (feature: string) => {
    if (saving) return;
    const next = features.filter((f) => f !== feature);
    await commit(next);
  };

  const onSuggestion = async (s: string) => {
    if (atCap || saving) return;
    const next = [...features, s];
    setInput("");
    setAdding(false);
    pushRecent(s, setRecent);
    await commit(next);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void onAdd();
    } else if (e.key === "Escape") {
      setAdding(false);
      setInput("");
    } else if (e.key === "Backspace" && input === "") {
      setAdding(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        {features.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => void onRemove(f)}
            disabled={saving}
            className="group inline-flex items-center gap-1.5 h-7 pl-2.5 pr-1.5 rounded-full bg-slate-100 text-[13px] text-slate-700 hover:bg-slate-200 active:bg-slate-300 transition disabled:opacity-50"
            aria-label={`Remove feature ${f}`}
          >
            <span>{f}</span>
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              aria-hidden="true"
              className="text-slate-400 group-hover:text-slate-600"
            >
              <path
                d="M3 3L9 9M9 3L3 9"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        ))}
        {!adding && !atCap && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            disabled={saving}
            className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full border border-dashed border-slate-300 text-[13px] text-slate-500 hover:bg-slate-50 active:bg-slate-100 transition disabled:opacity-50"
          >
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true">
              <path
                d="M5.5 1.5v8M1.5 5.5h8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            <span>Add feature</span>
          </button>
        )}
        {adding && (
          <div className="inline-flex items-center h-7 rounded-full bg-white border border-slate-300 pl-2.5 pr-1 gap-1">
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value.slice(0, FEATURE_MAX_LEN))}
              onKeyDown={onKeyDown}
              onBlur={() => {
                if (input === "") setAdding(false);
              }}
              className="w-32 outline-none text-[13px] text-slate-900 bg-transparent"
              placeholder="e.g. Threaded barrel"
              aria-label="New feature"
              maxLength={FEATURE_MAX_LEN}
              disabled={saving}
            />
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault() /* keep input focused */}
              onClick={() => void onAdd()}
              disabled={!canAdd}
              className="h-5 px-1.5 rounded-full text-[12px] font-semibold text-[#007aff] disabled:text-slate-300 transition"
              aria-label="Add"
            >
              Add
            </button>
          </div>
        )}
      </div>

      {adding && suggestions.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          <span className="text-[11px] uppercase tracking-wide text-slate-400 pr-1">
            {suggestions[0]?.source === "recent" ? "Recent" : "Suggested"}
          </span>
          {suggestions.map((s) => (
            <button
              key={`${s.source}-${s.value}`}
              type="button"
              // onMouseDown prevents input blur from closing the editor before
              // we handle the click (fast tap on iOS fires blur immediately).
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => void onSuggestion(s.value)}
              disabled={saving || atCap}
              className="inline-flex items-center h-7 px-2.5 rounded-full bg-slate-50 text-[13px] text-slate-600 hover:bg-slate-100 active:bg-slate-200 transition disabled:opacity-50"
            >
              {s.value}
            </button>
          ))}
        </div>
      )}

      {(error || atCap) && (
        <p className={`text-[12px] ${error ? "text-[#ff3b30]" : "text-slate-400"}`}>
          {error ?? `Up to ${FEATURES_MAX_COUNT} features.`}
        </p>
      )}
    </div>
  );
}

function readRecent(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is string => typeof x === "string").slice(0, RECENT_MAX);
  } catch {
    return [];
  }
}

function pushRecent(entry: string, setRecent: (next: string[]) => void) {
  if (typeof window === "undefined") return;
  const existing = readRecent();
  // Case-insensitive dedup; move to front.
  const filtered = existing.filter((e) => e.toLowerCase() !== entry.toLowerCase());
  const next = [entry, ...filtered].slice(0, RECENT_MAX);
  try {
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // storage full / disabled — not fatal.
  }
  setRecent(next);
}

function messageForError(res: EditResult<unknown> & { ok: false }): string {
  switch (res.error) {
    case "version_conflict":
      return "Someone else just edited this item. Refresh to see the latest.";
    case "validation_failed":
      if (res.reason === "too_long") return `Feature over ${FEATURE_MAX_LEN} characters.`;
      if (res.reason === "too_many") return `Up to ${FEATURES_MAX_COUNT} features.`;
      if (res.reason === "empty_after_trim") return "Feature can't be blank.";
      return "That value isn't valid.";
    case "not_found":
      return "This item no longer has firearm attributes.";
    default:
      return "Couldn't save. Try again in a moment.";
  }
}
